<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import FormField from '@/components/FormField.vue'
import EyesOpenIcon from '@/svg/eyes-open.svg'
import EyesCloseIcon from '@/svg/eyes-close.svg'
import { memberPinCommitApi } from '@/api'
import { useAssetsStore } from '@/stores/assets'
import { toastify } from '@/utils'

const props = defineProps<{
  show: boolean
}>()

const emit = defineEmits<{
  'update:show': [value: boolean]
  completed: []
}>()

const assetsStore = useAssetsStore()

const newPin = ref('')
const confirmPin = ref('')
const submitting = ref(false)

const pinVisible = reactive({
  new: false,
  confirm: false,
})

const visible = computed({
  get: () => props.show,
  set: (value) => emit('update:show', value),
})

const isPinValid = (value: string) => value.length === 6 && /^\d+$/.test(value)

const newPinErrorMessage = computed(() => {
  if (!newPin.value) return ''
  if (!isPinValid(newPin.value)) return '密码请输入6位数字'
  return ''
})

const confirmPinErrorMessage = computed(() => {
  if (!confirmPin.value) return ''
  if (!isPinValid(confirmPin.value)) return '密码请输入6位数字'
  if (newPin.value && confirmPin.value !== newPin.value) return '两次密码不一致'
  return ''
})

const canSubmit = computed(
  () =>
    isPinValid(newPin.value) &&
    isPinValid(confirmPin.value) &&
    newPin.value === confirmPin.value &&
    !submitting.value,
)

watch(visible, (value) => {
  if (!value) resetForm()
})

function sanitizePinInput(value: string) {
  return value.replace(/\D/g, '').slice(0, 6)
}

watch(newPin, (value) => {
  const next = sanitizePinInput(value)
  if (next !== value) newPin.value = next
})

watch(confirmPin, (value) => {
  const next = sanitizePinInput(value)
  if (next !== value) confirmPin.value = next
})

function resetForm() {
  newPin.value = ''
  confirmPin.value = ''
  submitting.value = false
  pinVisible.new = false
  pinVisible.confirm = false
}

const handleSubmit = async () => {
  if (!canSubmit.value) return

  submitting.value = true
  try {
    await memberPinCommitApi({ new_pin: newPin.value })
    await assetsStore.refreshProfile()
    toastify({ type: 'success', message: '支付密码设置成功' })
    emit('completed')
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : '支付密码设置失败'
    toastify({ type: 'danger', message })
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <van-popup
    v-model:show="visible"
    position="bottom"
    round
    safe-area-inset-bottom
    closeable
    :close-on-click-overlay="false"
    class="set-pin-popup"
  >
    <div class="flex flex-col">
      <div class="relative flex items-center justify-center h-12 border-b border-border-light">
        <h3 class="text-base font-semibold text-text-primary m-0">设置支付密码</h3>
      </div>

      <div class="px-4 pt-4 pb-2">
        <van-cell-group :border="false">
          <div>
            <form-field
              v-model="newPin"
              name="newPin"
              label-width="0"
              :type="pinVisible.new ? 'digit' : 'password'"
              inputmode="numeric"
              placeholder="请输入新的支付密码"
              maxlength="6"
              clearable
            >
              <template #button>
                <EyesOpenIcon
                  v-if="pinVisible.new"
                  class="size-5 cursor-pointer shrink-0"
                  @click.stop="pinVisible.new = false"
                />
                <EyesCloseIcon
                  v-else
                  class="size-5 cursor-pointer shrink-0"
                  @click.stop="pinVisible.new = true"
                />
              </template>
            </form-field>
            <p v-if="newPinErrorMessage" class="field-external-error" role="alert">
              {{ newPinErrorMessage }}
            </p>
          </div>

          <div class="confirm-pin-field-block">
            <form-field
              v-model="confirmPin"
              name="confirmPin"
              class="mt-4"
              label-width="0"
              :type="pinVisible.confirm ? 'digit' : 'password'"
              inputmode="numeric"
              placeholder="请再次输入新密码"
              maxlength="6"
              clearable
            >
              <template #button>
                <EyesOpenIcon
                  v-if="pinVisible.confirm"
                  class="size-5 cursor-pointer shrink-0"
                  @click.stop="pinVisible.confirm = false"
                />
                <EyesCloseIcon
                  v-else
                  class="size-5 cursor-pointer shrink-0"
                  @click.stop="pinVisible.confirm = true"
                />
              </template>
            </form-field>
            <p v-if="confirmPinErrorMessage" class="field-external-error" role="alert">
              {{ confirmPinErrorMessage }}
            </p>
          </div>
        </van-cell-group>

        <div class="mt-6">
          <van-button
            type="primary"
            size="large"
            block
            :loading="submitting"
            :disabled="!canSubmit"
            @click="handleSubmit"
          >
            确认
          </van-button>
        </div>
      </div>
    </div>
  </van-popup>
</template>

<style scoped>
.field-external-error {
  margin: 6px 0 0;
  padding: 0 4px;
  font-size: 12px;
  line-height: 1.4;
  color: var(--van-danger-color);
}
</style>
